import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { KbArticle } from '@/mocks/adminKnowledgeBase';
import { categoryMeta, kbArticles } from '@/mocks/adminKnowledgeBase';

interface ArticleDetailModalProps {
  article: KbArticle;
  onClose: () => void;
  onNavigate: (article: KbArticle) => void;
}

const formatter = new Intl.NumberFormat('es');

export default function ArticleDetailModal({ article, onClose, onNavigate }: ArticleDetailModalProps) {
  const [helpfulState, setHelpfulState] = useState<'helpful' | 'not_helpful' | null>(null);
  const meta = categoryMeta[article.category];

  const categoryArticles = kbArticles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const relatedArticles = kbArticles
    .filter((a) => a.category !== article.category && a.id !== article.id)
    .sort(() => Math.random() - 0.5)
    .slice(0, 2);

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-xl ${meta.bgColor} flex items-center justify-center shrink-0`}>
              <i className={`${meta.icon} ${meta.color} text-base`}></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-foreground-950 font-bold text-sm truncate">{article.title}</h3>
              <p className="text-foreground-400 text-[10px]">{meta.label} · {article.readTime} de lectura · {formatter.format(article.views)} vistas</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0 ml-3"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-5 md:p-6">
            <div
              className="prose prose-sm max-w-none text-foreground-800 leading-relaxed
                [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-foreground-950 [&_h1]:mb-4 [&_h1]:mt-6
                [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mb-3 [&_h2]:mt-5
                [&_h3]:text-sm [&_h3]:font-bold [&_h3]:text-foreground-800 [&_h3]:mb-2 [&_h3]:mt-4
                [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3
                [&_ul]:text-sm [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:mb-3
                [&_ol]:text-sm [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_ol]:mb-3
                [&_li]:leading-relaxed
                [&_strong]:text-foreground-900
                [&_code]:bg-background-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:text-rose-600 [&_code]:font-mono
                [&_blockquote]:border-l-2 [&_blockquote]:border-primary-300 [&_blockquote]:pl-4 [&_blockquote]:py-1 [&_blockquote]:my-3 [&_blockquote]:text-foreground-600 [&_blockquote]:text-sm [&_blockquote]:bg-background-50 [&_blockquote]:rounded-r-lg
                [&_table]:w-full [&_table]:text-xs [&_table]:border-collapse [&_table]:mb-3
                [&_th]:bg-background-100 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground-800 [&_th]:border [&_th]:border-background-200
                [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-background-200 [&_td]:text-foreground-700
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          {/* Helpful / Not helpful */}
          <div className="px-5 md:px-6 pb-4">
            <div className="border-t border-background-100 pt-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-foreground-600">¿Te fue útil este artículo?</p>
                  <button
                    onClick={() => setHelpfulState(helpfulState === 'helpful' ? null : 'helpful')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap border ${
                      helpfulState === 'helpful'
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                        : 'bg-background-100 text-foreground-500 border-background-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200'
                    }`}
                  >
                    <i className={`${helpfulState === 'helpful' ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-xs`}></i>
                    Sí
                  </button>
                  <button
                    onClick={() => setHelpfulState(helpfulState === 'not_helpful' ? null : 'not_helpful')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap border ${
                      helpfulState === 'not_helpful'
                        ? 'bg-red-100 text-red-700 border-red-300'
                        : 'bg-background-100 text-foreground-500 border-background-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                    }`}
                  >
                    <i className={`${helpfulState === 'not_helpful' ? 'ri-thumb-down-fill' : 'ri-thumb-down-line'} text-xs`}></i>
                    No
                  </button>
                </div>
                <Link
                  to="/admin/support"
                  onClick={onClose}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-customer-service-2-line text-xs"></i>
                  Contactar a Soporte
                </Link>
              </div>
              {helpfulState && (
                <p className="text-[10px] text-foreground-400 mt-2">
                  {helpfulState === 'helpful'
                    ? 'Gracias por tu feedback. Nos alegra que te haya servido.'
                    : 'Gracias por avisarnos. Si necesitás ayuda, contactá a soporte para asistencia personalizada.'}
                </p>
              )}
            </div>
          </div>

          {(categoryArticles.length > 0 || relatedArticles.length > 0) && (
            <div className="px-5 md:px-6 pb-5 border-t border-background-100 pt-4">
              {categoryArticles.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-foreground-800 font-bold text-xs mb-3">Más artículos en {meta.label}</h4>
                  <div className="space-y-2">
                    {categoryArticles.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => onNavigate(a)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer text-left group"
                      >
                        <div className={`w-8 h-8 rounded-lg ${meta.bgColor} flex items-center justify-center shrink-0`}>
                          <i className={`${meta.icon} ${meta.color} text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors truncate">{a.title}</p>
                          <p className="text-foreground-400 text-[10px]">{a.readTime} de lectura</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {relatedArticles.length > 0 && (
                <div>
                  <h4 className="text-foreground-800 font-bold text-xs mb-3">También te puede interesar</h4>
                  <div className="space-y-2">
                    {relatedArticles.map((a) => {
                      const rMeta = categoryMeta[a.category];
                      return (
                        <button
                          key={a.id}
                          onClick={() => onNavigate(a)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer text-left group"
                        >
                          <div className={`w-8 h-8 rounded-lg ${rMeta.bgColor} flex items-center justify-center shrink-0`}>
                            <i className={`${rMeta.icon} ${rMeta.color} text-sm`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors truncate">{a.title}</p>
                            <p className="text-foreground-400 text-[10px]">{rMeta.label} · {a.readTime}</p>
                          </div>
                          <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}